import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button } from "react-bootstrap";
import swal from "sweetalert";
import { aiService } from "../../service/api.service";
import { KnowledgeBaseStatusBadge } from "../common/StatusBadge";
import ReusableTable from "../common/ReusableTable";

const KNOWLEDGE_BASE_STATUS_OPTIONS = ["Done", "Processing", "Pending", "Failed"];

export default function KnowledgeBaseList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [knowledgeData, setKnowledgeData] = useState([]);
  const [agent, setAgent] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    if (location.state?.agent) {
      setAgent(location.state.agent);
    } else {
      navigate('/company-list');
    }
  }, [location, navigate]);

  useEffect(() => {
    if (agent) {
      fetchKnowledgeBase();
    }
  }, [agent, page, debouncedSearch, statusFilter]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  function handlePageChange(pageNumber) {
    setPage(pageNumber);
  }

  async function fetchKnowledgeBase() {
    setLoader(true);
    try {
      const res = await aiService.listKnowledgeBase({
        agent_id: agent._id,
        limit,
        page,
        search: debouncedSearch,
        status: statusFilter,
      });
      setKnowledgeData(res?.data?.data || []);
      setTotalItems(res?.data?.total_count || 0);
    } catch (error) {
      if (error?.response?.status === 401) {
        swal({ icon: 'error', text: 'Unauthorized', button: 'OK' }).then(() => {
          localStorage.clear();
          window.location.href = '/';
        });
      } else {
        swal({ icon: 'error', text: error?.response?.data?.message || 'Failed to load knowledge base.' });
      }
    } finally {
      setLoader(false);
    }
  }

  const handleDeleteAllKnowledge = () => {
    swal({
      title: 'Confirmation',
      text: 'Delete all knowledge base files for this agent? This action cannot be undone.',
      icon: 'warning',
      buttons: ['NO', 'YES'],
    }).then(async (val) => {
      if (!val) return;
      setLoader(true);
      try {
        await aiService.deleteKnowledgeBase({ agent_id: agent._id });
        swal({ icon: 'success', text: 'All knowledge base files deleted successfully.' });
        fetchKnowledgeBase();
      } catch (error) {
        if (error?.response?.status === 401) {
          swal({ icon: 'error', text: 'Unauthorized', button: 'OK' }).then(() => {
            localStorage.clear();
            window.location.href = '/';
          });
        } else {
          swal({ icon: 'error', text: error?.response?.data?.message || 'Failed to delete knowledge base.' });
        }
      } finally {
        setLoader(false);
      }
    });
  };

  const handleDeleteKnowledge = (kb) => {
    swal({
      title: 'Confirmation',
      text: `Delete knowledge base file "${kb.filename}"?`,
      icon: 'warning',
      buttons: ['NO', 'YES'],
    }).then(async (val) => {
      if (!val) return;
      setLoader(true);
      try {
        await aiService.deleteKnowledgeBase({ agent_id: agent._id, knowledge_base_id: kb._id });
        swal({ icon: 'success', text: 'Knowledge base deleted successfully.' });
        fetchKnowledgeBase();
      } catch (error) {
        if (error?.response?.status === 401) {
          swal({ icon: 'error', text: 'Unauthorized', button: 'OK' }).then(() => {
            localStorage.clear();
            window.location.href = '/';
          });
        } else {
          swal({ icon: 'error', text: error?.response?.data?.message || 'Failed to delete knowledge base.' });
        }
      } finally {
        setLoader(false);
      }
    });
  };

  return (
    <div className="dashboard-main-area company-list-area">
      <Container fluid>
        <div className="dashboard-area-heading management-heading">
          <div className="under-content-section">
            <div>
              <h1 className="company-page-title">
                See Knowledgebase
              </h1>
            </div>
            <div className="header-actions-right">
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeleteAllKnowledge}
              >
                Delete All
              </Button>
            </div>
          </div>
          <Row className="pb-4 justify-content-center">
            <Col md={12}>
              <ReusableTable
                columns={[
                  { header: 'Filename', accessor: 'filename' },
                  { header: 'Status', render: (kb) => <KnowledgeBaseStatusBadge status={kb.status} /> },
                  { header: 'Owner', accessor: 'owner_name' },
                  { header: 'Uploaded By', accessor: 'uploaded_by_name' },
                  { header: 'Uploaded At', render: (kb) => (kb.created_at ? new Date(kb.created_at).toLocaleString() : '-') },
                  { header: 'Delete', render: (kb) => (
                      <Button size="sm" variant="light" onClick={() => handleDeleteKnowledge(kb)}>
                        <i className="fa fa-trash text-danger" aria-hidden="true"></i>
                      </Button>
                    )
                  },
                ]}
                data={knowledgeData}
                loading={loader}
                searchValue={search}
                onSearch={setSearch}
                filters={[{ key: 'status', label: 'Status', options: KNOWLEDGE_BASE_STATUS_OPTIONS, value: statusFilter, onChange: setStatusFilter }]}
                pagination={{ activePage: page, itemsPerPage: limit, totalItems: totalItems, onChange: handlePageChange }}
                tableProps={{ className: 'UserListTable premium-user-list-table knowledge-base-table' }}
              />
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
}
